/**
 * Chinese, the only keyboard here that has to ask.
 *
 * ── Why this one is different ───────────────────────────────────────────────
 * Every other engine in this product is a function. Russian is one key to one
 * letter, Korean is three keys to one syllable by rule, Japanese romaji is
 * spelling to kana by rule — each has exactly one right answer per keystroke,
 * and each fits in a table small enough to ship.
 *
 * `zhong` has a hundred right answers. Nothing in the keystrokes says which,
 * and no rule will ever say which, so this engine does what every Chinese IME
 * does: it holds the Latin, offers the candidates in the order people actually
 * use them, and waits. The dictionary behind that ordering is built by
 * scripts/build-pinyin.mjs and downloaded once, the first time somebody opens
 * the Chinese keyboard — see `loadPinyin`.
 *
 * ── The shape of a session ──────────────────────────────────────────────────
 *   type `z` `h` `o` `n` `g`   → the Latin sits in the textarea, candidates
 *                                 appear: 中 种 重 众 …
 *   type `g` `u` `o`           → the spelling now matches a WORD, so 中国 is
 *                                 offered ahead of the single characters
 *   press space or `1`         → 中国 replaces `zhongguo` in the box
 *
 * The pending Latin lives IN the textarea rather than in a floating
 * pre-edit box, exactly as the Korean and Japanese engines do it. It is the
 * one decision all four share and it is worth stating: the reader can always
 * see everything the keyboard is holding, and if they navigate away, close the
 * panel or hit Escape, what they typed is still text rather than lost state.
 */

import type { ImeEdit } from './keyboards';

export interface PinyinDict {
  /** Syllable → the characters that read it, commonest first. */
  chars: Record<string, string>;
  /** Spelling with no tones or spaces → words, space separated, commonest first. */
  words: Record<string, string>;
  /** Every syllable the segmenter may split on. */
  syllables: string[];
  /** The commonest characters, in order — the ranking for a partial spelling. */
  order: string;
}

let dict: PinyinDict | null = null;
let loading: Promise<PinyinDict> | null = null;
let syllableSet: Set<string> = new Set();
/** Character → its place in `order`, so a partial spelling can rank its guesses. */
let rank: Map<string, number> = new Map();
/** The longest syllable in the table — `zhuang`, six — so the greedy segmenter
    knows where to stop guessing rather than scanning the whole buffer. */
let maxSyllable = 6;

/**
 * Fetch the dictionary, once per page.
 *
 * 420 KB, which is a quarter of a megabyte over the wire and far too much to
 * put in the bundle for the ninety-nine visits out of a hundred that are not
 * Chinese. So it is a plain file under `public/` and a plain `fetch`, and it
 * only ever happens after somebody has named Chinese as their input language
 * AND left the keyboard on.
 *
 * The promise is cached rather than the result, so two components mounting at
 * once make one request. A failure clears the cache — a dictionary that failed
 * to arrive must not be remembered as "there is no dictionary", or the
 * keyboard is dead until a reload.
 */
export function loadPinyin(): Promise<PinyinDict> {
  if (dict) return Promise.resolve(dict);
  if (!loading) {
    loading = fetch('/pinyin.json')
      .then((r) => {
        if (!r.ok) throw new Error(`pinyin dictionary: ${r.status}`);
        return r.json();
      })
      .then((d: PinyinDict) => {
        dict = d;
        syllableSet = new Set(d.syllables);
        rank = new Map([...(d.order ?? '')].map((ch, i) => [ch, i]));
        maxSyllable = d.syllables.reduce((n, s) => Math.max(n, s.length), 1);
        return d;
      })
      .catch((err) => {
        loading = null;
        throw err;
      });
  }
  return loading;
}

export function pinyinReady(): PinyinDict | null {
  return dict;
}

/**
 * Split a run of letters into pinyin syllables, greedily and longest-first.
 *
 * Greedy is not always right — `xian` is one syllable (先) and also `xi` + `an`
 * (西安) — and no segmenter without the rest of the sentence can settle that.
 * Longest-first is the convention every pinyin IME uses and the one that is
 * right more often, and the candidate list covers the rest: the reader who
 * meant 西安 sees it, because the word lookup below tries the WHOLE buffer
 * before it trusts this at all.
 *
 * Returns null the moment a piece is not a syllable, which is how a typo stops
 * producing candidates instead of producing nonsense ones.
 */
export function segment(latin: string): string[] | null {
  const out: string[] = [];
  let i = 0;
  while (i < latin.length) {
    let hit = '';
    for (let n = Math.min(maxSyllable, latin.length - i); n > 0; n -= 1) {
      const piece = latin.slice(i, i + n);
      if (syllableSet.has(piece)) { hit = piece; break; }
    }
    if (!hit) return null;
    out.push(hit);
    i += hit.length;
  }
  return out.length ? out : null;
}

export interface Candidate {
  /** What goes into the text. */
  text: string;
  /** How many characters of the pending Latin this consumes. */
  consumes: number;
}

/** How many candidates are worth drawing. Nine, so they are all one keypress away. */
const PAGE = 9;

/**
 * What to offer for the Latin currently pending.
 *
 * Four sources, in this order, and the order is the whole design:
 *
 *   1. WORDS SPELLING THE WHOLE BUFFER. `zhongguo` → 中国. This must come
 *      first or a two-syllable word is unreachable: the reader would pick 中
 *      after the first syllable and never see 中国 at all.
 *   2. WORDS SPELLING A PREFIX, longest prefix first. Typing a whole phrase
 *      and taking it a word at a time is how people actually type Chinese.
 *   3. CHARACTERS FOR THE FIRST SYLLABLE. The fallback that always exists.
 *   4. CHARACTERS FOR THE WHOLE BUFFER when it is one syllable — the same
 *      thing as (3) and listed separately only because (3) has to run when the
 *      buffer is several syllables and none of them formed a word.
 *
 * Duplicates are dropped as they are found, so a word that is also a prefix
 * match does not appear twice with different `consumes`.
 */
export function candidatesFor(latin: string): Candidate[] {
  if (!dict || !latin) return [];
  const out: Candidate[] = [];
  const seen = new Set<string>();
  const add = (text: string, consumes: number) => {
    if (!text || seen.has(text)) return;
    seen.add(text);
    out.push({ text, consumes });
  };

  const sylls = segment(latin);

  // (1) and (2): words, longest spelling first.
  if (sylls) {
    for (let take = sylls.length; take >= 2; take -= 1) {
      const key = sylls.slice(0, take).join('');
      const found = dict.words[key];
      if (found) for (const w of found.split(' ')) add(w, key.length);
      if (out.length >= PAGE) break;
    }
  }

  // (3): the first syllable, as characters.
  const first = sylls ? sylls[0] : null;
  if (first) for (const ch of dict.chars[first] ?? '') add(ch, first.length);

  /*
   * A BUFFER THAT DOES NOT SEGMENT IS NOT A DEAD END, and getting this wrong
   * makes the whole engine feel broken.
   *
   * `zhong` is five keystrokes and only the last one is a syllable. An engine
   * that offers nothing until a syllable is complete shows an empty bar for
   * `z`, `zh`, `zho`, `zhon` — four fifths of the typing — and then everything
   * at once, which reads as a control that does not work rather than one that
   * is waiting.
   *
   * So a partial spelling offers the characters of every syllable it could
   * still BECOME, ranked globally (`order`) rather than per syllable, because
   * they are now competing with each other rather than sitting in one list.
   * The whole buffer is consumed by such a pick, which is right: choosing 中
   * from a half-typed `zho` means that is the character you wanted.
   */
  if (!first) {
    const guesses: string[] = [];
    for (const syl of dict.syllables) {
      if (!syl.startsWith(latin)) continue;
      for (const ch of dict.chars[syl] ?? '') guesses.push(ch);
      if (guesses.length > 200) break;
    }
    guesses
      .sort((a, b) => (rank.get(a) ?? 9999) - (rank.get(b) ?? 9999))
      .forEach((ch) => add(ch, latin.length));
  }

  return out.slice(0, PAGE);
}

export interface PinyinPress {
  edit: ImeEdit;
  pending: string;
}

/** Add one letter to the pending Latin. It stays visible in the box. */
export function typePinyin(pending: string, key: string): PinyinPress {
  const next = pending + key.toLowerCase();
  return { edit: { back: pending.length, insert: next }, pending: next };
}

/**
 * Take a candidate: it replaces the Latin it spells, and the rest stays pending.
 *
 * The leftover is what makes typing a phrase work. Somebody types
 * `wodemingzi`, picks 我的 off the top, and `mingzi` is still in the box with
 * 名字 now offered — no retyping, and no need to have known where the word
 * boundaries were before they started.
 */
export function choosePinyin(pending: string, candidate: Candidate): PinyinPress {
  const rest = pending.slice(candidate.consumes);
  return { edit: { back: pending.length, insert: candidate.text + rest }, pending: rest };
}

/**
 * Give up on the pending Latin and leave it as Latin.
 *
 * What Escape does, and what happens when the language or the mode changes
 * underneath a half-typed word. Deliberately NOT "take the first candidate":
 * a reader who pressed Escape said no, and answering that by choosing for them
 * is the one thing an IME must never do.
 */
export function flushPinyin(pending: string): string {
  return pending;
}
